import { GraphQLMutationSignedClass } from "@eco/client";
import useEcoClient from "hooks/useEcoClient";
import _ from "lodash";
import { useCallback } from "react"
import useEcoMutation, { useEcoMutationOptions } from "./useEcoMutation";
import { useFindObjectPropertyId } from "./useFindObjectPropertyId";
import { useFindObjectRelationProperty } from "./useFindObjectRelationProperty";
import { useMutationDataDifference } from "./useMutationDataDifference";
import { useMutationRelationRemoved } from "./useMutationRelationRemoved";

export const useGenericMutation = <TData extends { __typename?: string }, TError = any>(
        data: TData,
        config?: useEcoMutationOptions<GenericMutationResponse<TData>, TError, GraphQLMutationSignedClass<TData>['variables']>
    ) => {

    const ecoClient = useEcoClient();
    const findPropertyId = useFindObjectPropertyId()
    const findRelationPropertyId = useFindObjectRelationProperty()
    const getDiff = useMutationDataDifference()
    const getRelationRemoved = useMutationRelationRemoved()

    const getGrapqhlFields = useCallback((_data: TData): string => {
        return Object.keys(_data).reduce<string>((result, key) => {
            const item = _data[key]

            if (key === '__typename') {
                return result
            }
            
            if (Array.isArray(item) && item.length > 0) {
                return `${result} ${key} { ${ getGrapqhlFields(item[0]) } }`
            } 

            if (!Array.isArray(item) && item !== null && typeof item === 'object') {
                return `${result} ${key} { ${ getGrapqhlFields(item) } }`
            }

            if (Array.isArray(item) || (item !== null && typeof item === 'object')) {
                return result
            }

            return `${result} ${key}`
        }, '')
    }, [])

    const getGraphqlRelationRemoved = useCallback((relations: Array<number | string>, relationName: string, oldData: TData) => {
        const propId = findPropertyId(oldData)
        const removed = oldData[relationName]?.find((item: any) => {
            const relationPropId = findRelationPropertyId(item)
            return relations.includes(item[relationPropId!])
        })

        return `
            removed${_.upperFirst(relationName)}: removeRelationFrom${oldData.__typename}(
                id: $id
                relationName: "${relationName}" 
                relationIds: [${ relations.join(',') }]
            ) {
                ${ propId }
                ${relationName} {
                    ${ removed ? getGrapqhlFields(removed) : '' }
                }
            }
        `
    }, [])

    const generateGql = useCallback((oldData: TData, updatedData: TData, relationRemoved?: Record<string, Array<number | string>>, autoRemoveRelation?: boolean) => {
        const isDataEmpty = _.isEmpty(_.omit(updatedData, ['__typename']))
        return `
            mutation ($id: Long, ${!isDataEmpty ? `$data: JSONObject` : '' }) {
                ${
                    !isDataEmpty
                    ? `
                        save${updatedData.__typename}(
                            id: $id
                            data: $data
                        ) {
                            ${ getGrapqhlFields(updatedData) }
                        }
                    `
                    : ''
                }

                ${
                    autoRemoveRelation !== false && relationRemoved
                    ? Object.keys(relationRemoved).filter((key) => !_.isEmpty(relationRemoved[key])).map((key) => {
                        const item = relationRemoved[key]
                        return getGraphqlRelationRemoved(item, key, oldData)
                    })
                    : ''
                }
            }
        `
    }, [])

    const mutationFn = useCallback((variables: GenericMutationVariable<TData>): Promise<GenericMutationResponse<TData>> => {
        const propertyId = findPropertyId(variables?.data!)
        const updated = getDiff(data, variables?.data || {})
        const relationRemoved = getRelationRemoved(data, variables?.data || {})
        const id = propertyId ? variables?.data[propertyId] : null
        
        return ecoClient.graphql<GenericMutationResponse<TData>, GenericMutationVariable<TData>>({
            query: generateGql(
                data,
                { 
                    __typename: data.__typename, 
                    ...updated 
                } as TData, 
                relationRemoved,
                variables.autoRemoveRelations
            ),
            variables: {
                id,
                ...variables,
                data: _.omit(updated, ['__typename'])  as any,
            },
        })
    }, [data])

    return useEcoMutation<GenericMutationResponse<TData>, GenericMutationVariable<TData>, TError>(mutationFn, config)
}

type GenericMutationResponse<TData> = { 
    [key: string]: Array<TData> 
}

type GenericMutationVariable<TData> = GraphQLMutationSignedClass<TData>['variables'] & {
    /**
     * Flag telling to automatically remove relation that is not inside the new data to save
     */
    autoRemoveRelations?: boolean
}


